import { yupResolver } from '@hookform/resolvers/yup';
import { useValidateAddressMutation } from '@vfit/consumer/data-access';
import {
    getItalianMunicipalities,
    getItalianProvinces,
    ICountry,
    IProvince,
} from '@vfit/shared/data-access';
import { IAddressObject } from '@vfit/shared/models';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IPicked } from '../addressManual.models';
import {
    retrieveAddressObjectFromString,
    retrieveAddressString,
} from '../addressManual.utils';
import { schema } from '../validation';
const useAddressManual = ({
    cmsConfig,
    onSubmit,
    state,
}) => {
    const italianMunicipalities: ICountry[] = getItalianMunicipalities();
    const italianProvinces: IProvince[] = getItalianProvinces();
    const [isProvinceSelected, setIsProvinceSelected] = useState<boolean>(true);
    const [cityProvince, setCityProvince] = useState<string>(state?.city || '');
    const addressCardLabel = cmsConfig?.addressInputCard;
    const {
        watch,
        setValue,
        getValues,
        trigger,
        register,
        setError,
        clearErrors,
        resetField,
        formState: { errors, isValid },
    } = useForm<IAddressObject>({
        resolver: yupResolver(schema(cmsConfig?.addressErrors)),
        defaultValues: state,
        mode: 'onChange',
    });
    const displayStateOrProvinceValue = watch('stateOrProvince');

    const mutation = useValidateAddressMutation();

    const calculatedItalianMunicipalities = useMemo(
        () =>
            italianMunicipalities
                .filter((city) =>
                    displayStateOrProvinceValue !== '' ? city.province === displayStateOrProvinceValue : true
                )
                .map((municipality) => `${municipality.municipality} (${municipality.province})`),
        [displayStateOrProvinceValue]
    );
    const calculatedItalianProvinces = useMemo(
        () => italianProvinces.map((province) => `${province.long} (${province.short})`),
        [italianMunicipalities]
    );

    const onSelectProvince = (value: string) => {
        setValue('stateOrProvince', (value.match(/\((.*?)\)/) || '')[1]);
        if (cityProvince !== getValues().stateOrProvince) {
            setValue('city', '');
            setCityProvince('');
        }
        trigger('stateOrProvince');
        setIsProvinceSelected(value !== '');
    };

    const onSelectCity = (value: string) => {
        setValue('city', (value.match(/^[^(]+/) || ' ')[0].trim());
        if (value !== '') {
            setCityProvince((value.match(/\((.*?)\)/) || '')[1]);
        }
        trigger('city');
    };

    /**
     * TITOLO QUI
     * @param address IAddressObject
     * @function
     * This method automatically take the address from google
     * if not valid resetField if valid setValue
     */
    const checkAddress = (address: IAddressObject): boolean => {
        let isValidAddress = true;
        Object?.keys(address)?.forEach((key) => {
            if (address[key] === '') {
                resetField(key as IPicked);
                isValidAddress = false;
            } else {
                setValue(key as IPicked, address?.[`${key}`]);
            }
        });
        return isValidAddress;
    };

    const handleValidateAddress = async () => {
        const inputs = getValues();
        const formattedAddress = retrieveAddressString(inputs);

        const address = await retrieveAddressObjectFromString(formattedAddress, setError, errors);
        const picked = address?.picked;
        const pickedForMutation = address?.pikedForMutation;

        const payload = {
            city: inputs?.city || picked?.city,
            postalCode: inputs?.postalCode || picked?.postalCode,
            stateOrProvince: inputs?.stateOrProvince || picked?.stateOrProvince,
            street: inputs?.street || picked?.street,
            streetNumber: inputs?.streetNumber || picked?.streetNumber,
        } as IAddressObject;

        const withExtended = {
            region: pickedForMutation?.region || '',
            placeId: pickedForMutation?.placeId || '',
            latitude: pickedForMutation?.latitude || '',
            longitude: pickedForMutation?.longitude || '',
        };
        return { payload, withExtended };
    };

    const handleSubmit = async () => {
        const { payload, withExtended } = await handleValidateAddress();
        if (payload && checkAddress(payload)) {
            mutation.mutate({ ...payload, ...withExtended });
            if (isValid && Object.keys(withExtended).length >= 3) {
                onSubmit(payload, withExtended);
            }
        }
        clearErrors();
    };

    const getApiErrorMessage = (): string => {
        let message = '';
        // Object.keys(errors).forEach((errorKey) => {
        //     if (errors && errors?.[errorKey].message) {
        //         if (message === '') message = errors[errorKey].message;
        //     }
        // });
        Object.values(errors).forEach((error: any) => {
            if (error?.message && message === '') {
                message = error.message;
            }
        });
        if (message === '') message = "L'indirizzo inserito non è valido";
        return message;
    };

    return {
        addressCardLabel,
        errors,
        register,
        onSelectProvince,
        italianProvinces,
        calculatedItalianProvinces,
        italianMunicipalities,
        calculatedItalianMunicipalities,
        onSelectCity,
        displayStateOrProvinceValue,
        cityProvince,
        isProvinceSelected,
        handleSubmit,
        getApiErrorMessage,
        setCityProvince,
        isValid
    };
}
export default useAddressManual;